import React from 'react';
import { z } from 'zod';
import { motion } from 'motion/react';
import { QuestionnaireEvent, QuestionType } from '@/machines/types';
import {
  InputDropdownTemplate,
  inputDropdownItems,
  inputOptions,
  InputTemplate,
  convertFtToCm,
} from '@quantum/ui-molecules';
import { SelectButton } from '@/components/select-button';
import { MultiSelect, QuestionLayout, Interstitial, TextQuestion } from '..';
import { RenderSwitchProps } from './types';
import * as styles from './styles.module.scss';

const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === z.ZodIssueCode.invalid_type && issue.expected === 'number') return { message: 'Required' };
  if (issue.code === z.ZodIssueCode.invalid_type && issue.expected === 'string') return { message: 'Required' };
  return { message: ctx.defaultError };
};

z.setErrorMap(customErrorMap);

const duration = 0.2;
const variants = {
  exit: {
    opacity: 1,
    transition: {
      duration,
    },
  },
};

const dropDownKeys: { [key: string]: string } = {
  prodRecWeight: 'weight',
  prodRecHeight: 'height',
};

export const RenderSwitch: React.FC<RenderSwitchProps> = ({ question, sendEvent }) => {
  const { questionTypeString, choices, key, interstitial, text, subtitle, tooltip, isOptional, html } = question;
  const dropDownKey = dropDownKeys[key];
  const questionTypeStringReplace = key !== 'selfDescribedGender' ? questionTypeString : QuestionType.singleSelect;

  switch (questionTypeStringReplace) {
    case QuestionType.singleSelect: {
      if (interstitial) {
        const {
          header = '',
          ctaText = '',
          avatar: { image_url: imageUrl = '', name = '', title = '' } = {},
          content = '',
          disclaimer,
        } = interstitial || {};

        return (
          <Interstitial
            header={header}
            ctaText={ctaText}
            avatarUrl={imageUrl}
            personName={name}
            jobTitle={title}
            quote={content}
            disclaimer={disclaimer}
            onClick={() =>
              sendEvent({ type: QuestionnaireEvent.ANSWERED, choices: choices[0], key, ignoreChoices: true })
            }
          />
        );
      }

      if (html) {
        return (
          <QuestionLayout tooltipContent={tooltip}>
            <TextQuestion
              htmlString={html}
              choice={choices[0]}
              onClick={() =>
                sendEvent({ type: QuestionnaireEvent.ANSWERED, choices: choices[0], key, ignoreChoices: true })
              }
            />
          </QuestionLayout>
        );
      }

      return (
        <QuestionLayout subtitle={subtitle} text={text} tooltipContent={tooltip}>
          <motion.div variants={variants}>
            {choices.map(choice => (
              <div key={`${choice.index}${choice.next.male.nextQuestionId || choice.index}`} className={styles.choice}>
                <SelectButton
                  text={choice.text}
                  onClick={() => sendEvent({ type: QuestionnaireEvent.ANSWERED, choices: choice, key })}
                />
              </div>
            ))}
          </motion.div>
        </QuestionLayout>
      );
    }
    case QuestionType.input: {
      return (
        <QuestionLayout subtitle={subtitle} text={text} tooltipContent={tooltip}>
          {inputDropdownItems[dropDownKey] ? (
            <InputDropdownTemplate
              key={dropDownKey}
              questionKey={dropDownKey}
              options={inputDropdownItems[dropDownKey]}
              onClick={({ inputValue = '', dropdownValue }) => {
                let selectedValue;

                if (dropDownKey === 'height') {
                  selectedValue = dropdownValue === 'ft' ? convertFtToCm(inputValue.toString()) : inputValue;
                }
                if (dropDownKey === 'weight') {
                  selectedValue =
                    dropdownValue === 'lb' ? +(parseInt(inputValue.toString(), 10) / 2.205).toFixed(0) : inputValue;
                }

                sendEvent({
                  type: QuestionnaireEvent.ANSWERED,
                  choices: { ...choices[0], selectedValue },
                  key,
                });
              }}
            />
          ) : (
            <InputTemplate
              key={key}
              field={key}
              mode={inputOptions[key]?.mode}
              type={inputOptions[key]?.type}
              rule={inputOptions[key] ? inputOptions[key].rule : inputOptions.default.rule}
              mask={inputOptions[key]?.mask}
              onClick={({ selectedValue = '' }) =>
                sendEvent({
                  type: QuestionnaireEvent.ANSWERED,
                  choices: { ...choices[0], selectedValue },
                  key,
                })
              }
            />
          )}
          {/* {isOptional && (
            <Text
              as='p'
              onClick={() =>
                sendEvent({ type: QuestionnaireEvent.ANSWERED, choices: choices[0], key, ignoreChoices: true })
              }
              className={styles.skip}
            >
              Skip to suggestions
            </Text>
          )} */}
        </QuestionLayout>
      );
    }
    case QuestionType.multiSelect: {
      return (
        <QuestionLayout
          subtitle={subtitle}
          text={text}
          titleMessage='Please select all that apply.'
          tooltipContent={tooltip}
        >
          <MultiSelect
            key={key}
            choices={choices}
            onClick={selectedAnswers => sendEvent({ type: QuestionnaireEvent.ANSWERED, choices: selectedAnswers, key })}
          />
        </QuestionLayout>
      );
    }
    default:
      return <div>not matched</div>;
  }
};
